import { Check, ChevronDown, Command, Search } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDashboard } from "../context/DashboardContext";
import { getClientLogoUrl } from "./social/socialBrand";
import { MobileSheet, SheetOption, useIsMobile } from "./social/mobile/MobileChrome";
import { useDocumentTheme } from "./social/useDocumentTheme";

function normalize(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("es");
}

export function ClientSelector() {
  const { data, selectedClient, setSelectedClient, loading, session } = useDashboard();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const isMobile = useIsMobile();
  const theme = useDocumentTheme();
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const selectedName =
    selectedClient === "ALL"
      ? "Toda la cartera"
      : data?.clients.find((client) => client.clientSlug === selectedClient)?.displayName ??
        selectedClient;

  const isLocked = Boolean(session?.isClientUser || session?.isClientPortal);

  const logoUrl = selectedClient === "ALL" ? null : getClientLogoUrl(selectedClient);
  const [logoFailed, setLogoFailed] = useState(false);

  useEffect(() => {
    setLogoFailed(false);
  }, [logoUrl]);

  const filteredClients = useMemo(() => {
    if (isLocked) return [];
    const term = normalize(query.trim());
    if (!term) return data?.clients ?? [];
    return (data?.clients ?? []).filter((client) =>
      normalize(`${client.displayName} ${client.clientSlug}`).includes(term),
    );
  }, [data, query, isLocked]);

  useEffect(() => {
    if (!open || isLocked || isMobile) return;

    const closeOnOutsideClick = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
        setQuery("");
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        setQuery("");
        triggerRef.current?.focus();
      }
    };

    document.addEventListener("pointerdown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open, isLocked, isMobile]);

  const chooseClient = (slug: string) => {
    setSelectedClient(slug);
    setOpen(false);
    setQuery("");
    requestAnimationFrame(() => triggerRef.current?.focus());
  };

  if (isLocked) {
    return (
      <div className="client-selector is-locked" title="Cliente asignado en portal">
        <div className="client-selector-trigger" style={{ cursor: "default", opacity: 0.9 }}>
          {logoUrl && !logoFailed ? (
            <span
              className="client-selector-name client-selector-logo"
              title={selectedName}
              style={
                selectedClient === "morapavic"
                  ? { backgroundColor: "rgba(7, 160, 174, .6)" }
                  : undefined
              }
            >
              <img
                src={logoUrl}
                alt={selectedName}
                onError={() => setLogoFailed(true)}
              />
            </span>
          ) : (
            <span className="client-selector-name">{selectedName}</span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={open ? "client-select is-open" : "client-select"} ref={rootRef}>
      <button
        ref={triggerRef}
        className="client-select-trigger"
        type="button"
        aria-label={`Cliente activo: ${selectedName}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={loading}
        onClick={() => setOpen((value) => !value)}
      >
        <Command size={16} aria-hidden="true" />
        <span>{selectedName}</span>
        <ChevronDown size={14} aria-hidden="true" />
      </button>

      {isMobile && (
        <MobileSheet
          open={open}
          title="Cliente"
          theme={theme}
          onClose={() => {
            setOpen(false);
            setQuery("");
          }}
        >
          <label className="client-search">
            <Search size={18} aria-hidden="true" />
            <span className="sr-only">Buscar cliente</span>
            <input
              value={query}
              placeholder="Buscar cliente"
              inputMode="search"
              onChange={(event) => setQuery(event.target.value)}
            />
          </label>
          <div className="sbx-m-options" role="radiogroup" aria-label="Clientes">
            {!query && (
              <SheetOption label="Toda la cartera" selected={selectedClient === "ALL"} onClick={() => chooseClient("ALL")} />
            )}
            {filteredClients.map((client) => (
              <SheetOption
                key={client.clientSlug}
                label={client.displayName}
                hint={client.alertCount > 0 ? `${client.alertCount} ${client.alertCount === 1 ? "alerta" : "alertas"}` : undefined}
                selected={selectedClient === client.clientSlug}
                onClick={() => chooseClient(client.clientSlug)}
              />
            ))}
            {!filteredClients.length && <p className="client-options-empty">No encontramos ese cliente.</p>}
          </div>
        </MobileSheet>
      )}

      {open && !isMobile && (
        <div className="client-select-popover">
          <label className="client-search">
            <Search size={15} aria-hidden="true" />
            <span className="sr-only">Buscar cliente</span>
            <input
              autoFocus
              value={query}
              placeholder="Buscar cliente..."
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && filteredClients[0]) {
                  chooseClient(filteredClients[0].clientSlug);
                }
              }}
            />
          </label>

          <div className="client-options" role="listbox" aria-label="Clientes">
            {!query && (
              <button
                type="button"
                role="option"
                aria-selected={selectedClient === "ALL"}
                className={selectedClient === "ALL" ? "is-selected" : ""}
                onClick={() => chooseClient("ALL")}
              >
                <span>
                  <strong>Toda la cartera</strong>
                  <small>Vista global</small>
                </span>
                {selectedClient === "ALL" && <Check size={15} aria-hidden="true" />}
              </button>
            )}

            {filteredClients.map((client) => (
              <button
                type="button"
                role="option"
                aria-selected={selectedClient === client.clientSlug}
                className={selectedClient === client.clientSlug ? "is-selected" : ""}
                key={client.clientSlug}
                onClick={() => chooseClient(client.clientSlug)}
              >
                <span>
                  <strong>{client.displayName}</strong>
                  <small>{client.alertCount} alertas</small>
                </span>
                {selectedClient === client.clientSlug && (
                  <Check size={15} aria-hidden="true" />
                )}
              </button>
            ))}

            {!filteredClients.length && (
              <p className="client-options-empty">No encontramos ese cliente.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
